import { useRef, useState } from "react";
import axios from "axios";
import { Camera, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import UserAvatar from "./UserAvatar";

/**
 * Avatar uploader for Account page. Click → file picker; auto-uploads.
 */
export default function AvatarUploader({ user, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pickFile = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Use JPG, PNG, or WEBP"); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/me/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profile picture updated");
      onChange?.(data.avatar_url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeAvatar = async () => {
    if (!window.confirm("Remove profile picture?")) return;
    try {
      await axios.delete(`${API}/me/avatar`);
      onChange?.(null);
      toast.success("Removed");
    } catch (err) {
      toast.error("Could not remove");
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid="avatar-uploader">
      <div className="relative">
        <UserAvatar user={user} size={88} ring="ring-2 ring-white/10" />
        <button
          onClick={pickFile}
          disabled={uploading}
          className="absolute -bottom-1 -right-1 p-2 rounded-full bg-[#0047FF] hover:bg-[#003BE0] border-2 border-[#050505] shadow-lg transition-colors disabled:opacity-50"
          data-testid="avatar-upload-btn"
          aria-label="Upload profile picture"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" /> : <Camera className="w-3.5 h-3.5 text-white" />}
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-base font-bold text-white truncate" style={{ fontFamily: "Outfit, system-ui" }}>
          {[user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email}
        </p>
        <p className="text-xs text-white/50 truncate">{user?.email}</p>
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={pickFile}
            disabled={uploading}
            className="text-[11px] uppercase tracking-wider text-[#4D88FF] font-bold hover:underline disabled:opacity-50"
            data-testid="avatar-change-link"
          >
            {user?.avatar_url ? "Change picture" : "Upload picture"}
          </button>
          {user?.avatar_url && (
            <button
              onClick={removeAvatar}
              className="text-[11px] uppercase tracking-wider text-white/40 font-bold hover:text-red-300 flex items-center gap-1"
              data-testid="avatar-remove-link"
            >
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          )}
        </div>
      </div>
      <input
        type="file"
        ref={inputRef}
        onChange={handleFile}
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        data-testid="avatar-file-input"
      />
    </div>
  );
}
